import * as React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Divider,
  IconButton,
  Typography,
  Switch
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";

type Props = {
  rows: any[];
  columns: GridColDef[];
  page: number;
  pageSize: number;
  rowCount: number;
  loading?: boolean;
  onView?: (row: any) => void;
  onDelete?: (row: any) => void;
  onReport?: (row: any) => void;
  onEnrolled?: (row: any) => void;
  onStatusToggle?: (row: any) => void;
  onPageChange?: (nextPage: number) => void;
};

const MobileAccordionTable: React.FC<Props> = ({
  rows,
  columns,
  page,
  pageSize,
  rowCount,
  loading = false,
  onView,
  onDelete,
  onReport,
  onEnrolled,
  onStatusToggle,
  onPageChange,
}) => {
  const start = rowCount === 0 ? 0 : page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, rowCount);
  const displayColumns = columns.filter(col => col.field !== 'actions');
  const mainColumn = displayColumns.find(col => col.field === 'fullName' || col.field === 'fullname' || col.field === 'className' || col.field === 'passName') || displayColumns[1];
  const snoColumn = displayColumns.find(col => col.field === 'sno' || col.field === 'serial');
  const detailColumns = displayColumns.filter(col => col.field !== snoColumn?.field && col.field !== mainColumn?.field);
  const getCellValue = (row: any, col: any) => {
    try {
      if (col.valueGetter) {
        const val = col.valueGetter({
          row,
          field: col.field,
          api: {} as any,
          id: row.id,
          value: row[col.field],
          colDef: col,
          cellMode: 'view',
          hasFocus: false,
          tabIndex: -1
        });
        return val !== undefined && val !== null ? val : "-";
      }
      return row[col.field] !== undefined && row[col.field] !== null ? row[col.field] : "-";
    } catch (error) {
      console.error("Error getting cell value:", error);
      return "-";
    }
  };

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2 }, mt: 1 }}>
      {/* Header strip */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 2fr 24px",
          alignItems: "center",
          py: 1.5,
          px: 2,
          borderBottom: "1px solid #E5E7EB",
          bgcolor: "#F9FAFB",
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Typography fontWeight={600} fontSize={13}>
          {snoColumn?.headerName || "S.No"}
        </Typography>
        <Typography fontWeight={600} fontSize={13}>
          {mainColumn?.headerName || "Name"}
        </Typography>
        <Box />
      </Box>

      {/* Body */}
      <Box
        sx={{
          border: "1px solid #E5E7EB",
          borderTop: 0,
          borderRadius: "0 0 8px 8px",
          overflow: "hidden",
          bgcolor: "#FFFFFF",
        }}
      >
        {rows.map((r, idx) => {
          let snoValue = snoColumn ? getCellValue(r, snoColumn) : null;
          // Fallback if snoValue is missing or invalid
          if (snoValue === "-" || snoValue === null || snoValue === undefined || (typeof snoValue === 'number' && isNaN(snoValue))) {
            const calculatedSNo = (Number(page) || 0) * (Number(pageSize) || 0) + idx + 1;
            snoValue = String(calculatedSNo).padStart(2, "0");
          }
          
          let mainValue = mainColumn ? getCellValue(r, mainColumn) : "-";

          return (
            <Accordion key={r.id} disableGutters square sx={{ boxShadow: "none" }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  "& .MuiAccordionSummary-content": {
                    m: 0,
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr",
                    gap: 2,
                    alignItems: "center",
                  },
                  px: 2,
                  py: 1.75,
                  bgcolor: "#FFFFFF",
                  borderBottom: "1px solid #E2EBD6",
                  "&.Mui-expanded": { bgcolor: "#FFFFFF" },
                  "& .MuiAccordionSummary-expandIconWrapper": {
                    color: "#6B7280",
                  },
                }}
              >
                <Typography fontWeight={500} color="#4B5563">
                  {snoValue}
                </Typography>
                <Tooltip title={String(mainValue)} arrow placement="top">
                  <Typography
                    fontWeight={500}
                    color="#4B5563"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "100%",
                      cursor: "pointer",
                    }}
                  >
                    {mainValue}
                  </Typography>
                </Tooltip>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, py: 1.5, bgcolor: "#FFFFFF" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    rowGap: 1,
                    columnGap: 3,
                  }}
                >
                  {detailColumns.map((col) => {
                    const val = getCellValue(r, col);
                    return (
                      <Box key={col.field}>
                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                          {col.headerName}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {(col.field === 'status' || col.field === 'isActive') && onStatusToggle ? (
                            <Switch
                              size="small"
                              checked={!!val}
                              onChange={() => onStatusToggle(r)}
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#9BCB4A" },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#9BCB4A" },
                                "& .MuiSwitch-track": { backgroundColor: "#D4D8DD" },
                                ml: -1
                              }}
                            />
                          ) : null}
                          <Typography sx={{ wordBreak: "break-all", lineHeight: 1.4, fontSize: '14px' }}>
                            {typeof val === 'boolean' ? (val ? "Active" : "Inactive") : (val ?? "-")}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                <Divider sx={{ my: 1.5, borderColor: "#E2EBD6" }} />

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Actions
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {onEnrolled && (
                      <IconButton
                        aria-label="enrolled"
                        onClick={() => onEnrolled?.(r)}
                        size="small"
                        sx={{
                          border: "1px solid #C8E6C9",
                          color: "#388E3C",
                          width: 28,
                          height: 28,
                        }}
                      >
                        <PeopleOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                    {onReport && (
                      <IconButton
                        aria-label="report"
                        onClick={() => onReport?.(r)}
                        size="small"
                        sx={{
                          border: "1px solid #C7D9F5",
                          color: "#1565C0",
                          width: 28,
                          height: 28,
                        }}
                      >
                        <AssessmentOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                    {onView && (
                      <IconButton
                        aria-label="view"
                        onClick={() => onView?.(r)}
                        size="small"
                        sx={{
                          border: "1px solid #D1E7C7",
                          color: "#A3D451",
                          width: 28,
                          height: 28,
                          "&:hover": { bgcolor: "rgba(11,77,186,0.08)" },
                        }}
                      >
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    )}
                    {onDelete && (
                      <IconButton
                        aria-label="delete"
                        onClick={() => onDelete?.(r)}
                        size="small"
                        sx={{
                          border: "1px solid #F2B6B6",
                          color: "#E53935",
                          width: 28,
                          height: 28,
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}

        {/* Pagination footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderTop: "1px solid",
            borderColor: "#E2EBD6",
            bgcolor: "#FFFFFF",
          }}
        >
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Typography variant="body2" color="#6B7280">
              {start} - {end} of {rowCount}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              disabled={page === 0 || loading}
              onClick={() => onPageChange?.(page - 1)}
              aria-label="previous page"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={(page + 1) * pageSize >= rowCount || loading}
              onClick={() => onPageChange?.(page + 1)}
              aria-label="next page"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(MobileAccordionTable);

